import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/react-hooks';
import { pathOr } from 'ramda';
import Lottie from 'lottie-react';

import { VERIFY_MAGIC_LINK } from 'apollo/mutations';
import { useTabSettings } from 'utils/hooks/useTabSettings';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { handleTrackEvent, identifyAnalyticsFromToken } from 'utils/analytics';
import { ROUTES } from 'utils/constants';
import { KEYS } from 'components/evieOnboarding/utils/constants';
import signupLogo from 'assets/images/signup-logo.webp';
import heartLoader from 'assets/heart-loader.json';

import Layout from 'components/evieOnboarding/Layout';
import SolidButton from 'components/SolidButton';

import '../EvieLogin/evie-login.scss';

const STATUS = {
  VERIFYING: 'verifying',
  INVALID: 'invalid',
  UNREACHABLE: 'unreachable',
  REDIRECTING: 'redirecting',
};
const VERIFY_TIMEOUT_MS = 15000;

// Lands the emailed magic link (EVI-89). Tokens are multi-use within their
// TTL (EVI-99), so redeeming on mount is safe, and this screen sends no email.
const EvieVerifyLogin = () => {
  useTabSettings('Evie', '/evie-favicon.ico');

  const { t } = useTranslation();
  const { push, replace } = useHistory();
  const { search } = useLocation();

  // Read once. The effect below strips the query string, so every later
  // render sees an empty search and must not re-derive this.
  const [linkToken] = useState(() => new URLSearchParams(search).get('token'));

  const [userToken, setUserToken] = useLocalStorage(KEYS.USER_TOKEN);
  const [verifyMagicLink] = useMutation(VERIFY_MAGIC_LINK);
  const [status, setStatus] = useState(() => {
    if (userToken) return STATUS.REDIRECTING;
    return linkToken ? STATUS.VERIFYING : STATUS.INVALID;
  });
  const attemptedRef = useRef(false);
  const timedOutRef = useRef(false);

  // The token must not linger in the address bar: page URLs are visible to
  // browser history and to anything that records them (the HelpScout beacon
  // loads on every route).
  useEffect(() => {
    if (!search) return;
    replace({ pathname: ROUTES.AUTH_VERIFY, search: '' });
  }, []);

  // A signed-in patient tapping any magic link (live or dead) should never
  // see this screen; the navigation itself must happen after mount, not
  // while rendering.
  useEffect(() => {
    if (status === STATUS.REDIRECTING) {
      replace(ROUTES.EVIE_ASSISTANT);
      return;
    }
    if (linkToken) startVerification();
  }, []);

  const startVerification = () => {
    if (attemptedRef.current) return;
    attemptedRef.current = true;

    const timeoutId = setTimeout(() => {
      timedOutRef.current = true;
      setStatus(STATUS.UNREACHABLE);
    }, VERIFY_TIMEOUT_MS);

    const verify = async () => {
      try {
        const response = await verifyMagicLink({ variables: { token: linkToken } });
        const sessionToken = pathOr(null, ['data', 'verifyMagicLink', 'token'])(response);
        if (sessionToken) {
          // A late success still signs her in, whatever is on screen.
          identifyAnalyticsFromToken(sessionToken);
          handleTrackEvent('magic_link_verified');
          await setUserToken(sessionToken);
          // Replace, not push: this route redeems on tap, so leaving it in
          // the stack means Back re-runs a consumed token and tells a
          // signed-in patient her link is invalid.
          replace(ROUTES.EVIE_ASSISTANT);
          return;
        }
        handleTrackEvent('magic_link_failed');
        // Already showing unreachable for this attempt; a late non-token
        // answer must not relabel it invalid.
        if (timedOutRef.current) return;
        setStatus(STATUS.INVALID);
      } catch {
        handleTrackEvent('magic_link_failed');
        // Apollo raises this for fetch failures, non-2xx and malformed
        // responses alike, so it says nothing about whether the token
        // survived. Report only what we know: sign-in did not complete.
        if (timedOutRef.current) return;
        setStatus(STATUS.UNREACHABLE);
      } finally {
        clearTimeout(timeoutId);
      }
    };

    verify();
  };

  const goToLogin = () => push(ROUTES.EVIE_LOGIN);

  const renderVerifying = () => (
    <p className="evie-login__container_description">{t('evieApp.magicLink.verifyingTitle')}</p>
  );

  const renderInvalid = () => (
    <>
      <h2 className="evie-login__container_title">{t('evieApp.magicLink.invalidTitle')}</h2>
      <p className="evie-login__container_description">{t('evieApp.magicLink.invalidDescription')}</p>
      <SolidButton type="button" className="evie-survey" onClick={goToLogin}>
        {t('evieApp.magicLink.backToLogin')}
      </SolidButton>
    </>
  );

  // We cannot know whether the token was spent, so the copy makes no claim
  // about it and the only offer is signing in again.
  const renderUnreachable = () => (
    <>
      <h2 className="evie-login__container_title">{t('evieApp.magicLink.unreachableTitle')}</h2>
      <p className="evie-login__container_description">{t('evieApp.magicLink.unreachableDescription')}</p>
      <SolidButton type="button" className="evie-survey" onClick={goToLogin}>
        {t('evieApp.magicLink.backToLogin')}
      </SolidButton>
    </>
  );

  return (
    <>
      <Layout>
        <main className="evie-login">
          <section className="evie-login__container">
            <img className="evie-login__container_logo" src={signupLogo} alt="Evie logo" />
            {(status === STATUS.VERIFYING || status === STATUS.REDIRECTING) && renderVerifying()}
            {status === STATUS.INVALID && renderInvalid()}
            {status === STATUS.UNREACHABLE && renderUnreachable()}
          </section>
        </main>
      </Layout>
      {status === STATUS.VERIFYING && (
        <div className="onboarding-layout__lottie">
          <Lottie animationData={heartLoader} loop style={{ width: 140, height: 110 }} />
        </div>
      )}
    </>
  );
};

export default EvieVerifyLogin;
